/**
 * Contact.jsx — EVORA Contact Us Page
 * Fully audited and rewritten.
 *
 * BUGS FIXED vs previous version:
 * 1. formData state race on success: email captured before reset so success
 *    message always shows the correct email address.
 * 2. <h1> moved inside <main> — was outside main landmark, hurting SEO.
 * 3. ct-form__hp parent had no position:relative — honeypot leaked layout.
 *    Now uses visibility:hidden so it's truly inert.
 * 4. organizationSchema.contactOption:'TollFree' removed — incorrect for
 *    WhatsApp/email, risks Google rich result penalty.
 * 5. Submit button width:100% on mobile — was narrow on 375px screens.
 * 6. input font-size corrected to 16px — 15px triggers iOS Safari zoom (CLS).
 * 7. Message char counter added — removes "why won't it submit?" friction.
 * 8. Captured email const used in success state before formData reset.
 * 9. og:image fallback added — /og-contact.jpg → /og-image.jpg if missing.
 * 10. meta robots added explicitly.
 * 11. WhatsApp sticky mobile CTA added.
 */

import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import { useToast } from '../../context/ToastContext';
import {
  IconWhatsApp,
  IconInstagram,
  IconArrowRight,
} from '../../components/common/Icons/Icons';
import './Contact.css';

// ── Constants ──────────────────────────────────────────────────
const SITE_URL    = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const CONTACT_URL = `${SITE_URL}/contact`;
const API_URL     = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WHATSAPP_NUMBER  = process.env.REACT_APP_WHATSAPP || '91XXXXXXXXXX';
const WHATSAPP_MESSAGE = encodeURIComponent('Hi! I have a question about my EVORA order.');
const WHATSAPP_HREF    = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
const CONTACT_EMAIL    = BRAND?.email || 'support@evoraclothing.com';
const BRAND_NAME       = BRAND?.name || 'EVORA';
const MSG_MIN          = 10;
const MSG_MAX          = 1000;

// ── JSON-LD: single @graph (BreadcrumbList, ContactPage, LocalBusiness, Organization) ──
const contactPageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      '@id': `${CONTACT_URL}#webpage`,
      url: CONTACT_URL,
      name: `Contact Us | ${BRAND_NAME}`,
      description: `Reach ${BRAND_NAME} for order help, returns, sizing, and enquiries. WhatsApp or email — we respond within 24 hours.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${CONTACT_URL}#breadcrumb` },
      mainEntity: { '@id': `${CONTACT_URL}#localbusiness` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${CONTACT_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Contact Us', item: CONTACT_URL },
      ],
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${CONTACT_URL}#localbusiness`,
      name: BRAND_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      image: `${SITE_URL}/og-image.jpg`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: CONTACT_EMAIL,
        availableLanguage: ['English', 'Hindi'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '10:00',
          closes: '19:00',
        },
      },
      sameAs: [BRAND?.instagram].filter(Boolean),
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: BRAND_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: CONTACT_EMAIL,
        availableLanguage: ['English', 'Hindi'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '10:00',
          closes: '19:00',
        },
      },
      sameAs: [BRAND?.instagram].filter(Boolean),
    },
  ],
};

const EMPTY_FORM = {
  name: '', email: '', orderNumber: '', subject: '', message: '', _hp: '',
};

const SUBJECTS = [
  { value: '',                  label: 'Select a topic…' },
  { value: 'Order Status',      label: 'Order status / tracking' },
  { value: 'Return / Exchange', label: 'Return or exchange' },
  { value: 'Size & Fit',        label: 'Size & fit questions' },
  { value: 'Payment Issue',     label: 'Payment issue' },
  { value: 'Product Question',  label: 'Product question' },
  { value: 'Other',             label: 'Other' },
];

// ── Quick answers data ─────────────────────────────────────────
const QUICK_LINKS = [
  { label: 'Common questions',   to: '/faq',             cta: 'View FAQs' },
  { label: 'Order status',       to: '/account/orders',  cta: 'View orders' },
  { label: 'Returns & exchanges',to: '/returns',         cta: 'Returns policy' },
  { label: 'Sizing help',        to: '/size-guide',      cta: 'Size guide' },
  { label: 'Delivery info',      to: '/shipping',        cta: 'Shipping info' },
];

// ══════════════════════════════════════════════════════════════
export default function Contact() {
  const { showToast }     = useToast();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading,  setLoading]  = useState(false);
  const [errors,   setErrors]   = useState({});
  const [sentTo,   setSentTo]   = useState(null); // FIX: store email before reset
  const [showWaSticky, setShowWaSticky] = useState(false);
  const formRef = useRef(null);

  // Show WhatsApp sticky after 1.5s (not immediately — avoids CLS)
  useState(() => {
    const t = setTimeout(() => setShowWaSticky(true), 1500);
    return () => clearTimeout(t);
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }, [errors]);

  const validate = useCallback(() => {
    const errs = {};
    if (!formData.name.trim())
      errs.name = 'Please enter your name';
    if (!formData.email.trim())
      errs.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errs.email = 'Please enter a valid email address';
    if (!formData.subject)
      errs.subject = 'Please select a topic';
    if (!formData.message.trim())
      errs.message = 'Please enter your message';
    else if (formData.message.trim().length < MSG_MIN)
      errs.message = `Message must be at least ${MSG_MIN} characters`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._hp) return; // honeypot
    if (!validate()) {
      showToast('Please fix the errors below and try again.', 'error');
      // Scroll to first error
      formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
      return;
    }
    setLoading(true);
    // FIX: capture email BEFORE resetting form to avoid state-race in success msg
    const submittedEmail = formData.email;
    try {
      const { _hp, ...payload } = formData;
      const res  = await fetch(`${API_URL}/contact`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to send message');
      setSentTo(submittedEmail);
      setFormData(EMPTY_FORM);
      setErrors({});
      showToast("Message sent! We'll be in touch within 24 hours.", 'success');
    } catch (err) {
      showToast(
        err.message || 'Could not send — try WhatsApp for a faster response.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const msgLen = formData.message.length;

  return (
    <>
      <Helmet>
        <title>Contact Us | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`Reach ${BRAND_NAME} for order help, returns, sizing, and enquiries. WhatsApp or email — we respond within 24 hours.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={CONTACT_URL} />

        <link rel="prefetch" href="/faq" as="document" />
        <link rel="prefetch" href="/returns" as="document" />
        <link rel="prefetch" href="/shipping" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={CONTACT_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Contact Us | ${BRAND_NAME}`} />
        <meta property="og:description" content={`Reach ${BRAND_NAME} for order help, returns, and enquiries. We respond within 24 hours.`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Contact ${BRAND_NAME} — WhatsApp & email support`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Contact Us | ${BRAND_NAME}`} />
        <meta name="twitter:description" content={`Reach ${BRAND_NAME} for order help. We respond within 24 hours.`} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.jpg`} />
        <meta name="twitter:image:alt" content={`Contact ${BRAND_NAME} — WhatsApp & email support`} />

        <script type="application/ld+json">{JSON.stringify(contactPageSchema)}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        {/* FIX: h1 is now inside <main> — was outside main landmark */}
        <main className="ct-page" id="main-content">

          {/* ── Breadcrumb ──────────────────────────────────────────── */}
          <nav className="ct-breadcrumb" aria-label="Breadcrumb">
            <ol className="ct-breadcrumb__list">
              <li><Link to="/" className="ct-breadcrumb__link">Home</Link></li>
              <li aria-hidden="true" className="ct-breadcrumb__sep">›</li>
              <li><span aria-current="page" className="ct-breadcrumb__current">Contact Us</span></li>
            </ol>
          </nav>

          {/* ── Hero — h1 lives here, inside <main> ─────────────────── */}
          <header className="ct-hero">
            <div className="ct-hero__inner">
              <p className="ct-hero__eyebrow" aria-hidden="true">Customer Support</p>
              <h1 className="ct-hero__title">How can we<br />help you?</h1>
              <p className="ct-hero__sub">
                Real people, real answers. WhatsApp is fastest — usually within a few hours.
              </p>
              {/* Above-fold WhatsApp CTA — visible without scrolling on mobile */}
              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="ct-hero__wa-btn"
                aria-label="Chat with us on WhatsApp"
              >
                <IconWhatsApp size={18} aria-hidden="true" />
                Chat on WhatsApp
              </a>
            </div>
            <div className="ct-hero__accent" aria-hidden="true" />
          </header>

          {/* ── Channels strip ──────────────────────────────────────── */}
          <section className="ct-channels-wrap" aria-label="Contact channels">
            <div className="ct-channels">
              <h2 id="ct-channels-heading" className="ct-sr-only">Contact channels</h2>

              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="ct-channel ct-channel--wa"
                aria-label="Chat with us on WhatsApp"
              >
                <span className="ct-channel__icon"><IconWhatsApp size={22} /></span>
                <span className="ct-channel__body">
                  <strong className="ct-channel__name">WhatsApp</strong>
                  <span className="ct-channel__time">Replies within a few hours</span>
                </span>
                <span className="ct-channel__arrow" aria-hidden="true"><IconArrowRight size={15} /></span>
              </a>

              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Support%20Request`}
                className="ct-channel ct-channel--email"
                aria-label={`Email us at ${CONTACT_EMAIL}`}
              >
                <span className="ct-channel__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-10 7L2 7"/>
                  </svg>
                </span>
                <span className="ct-channel__body">
                  <strong className="ct-channel__name">Email</strong>
                  <span className="ct-channel__time">{CONTACT_EMAIL} · 24–48 hr</span>
                </span>
                <span className="ct-channel__arrow" aria-hidden="true"><IconArrowRight size={15} /></span>
              </a>

              <div className="ct-channel ct-channel--hours" role="listitem">
                <span className="ct-channel__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                </span>
                <span className="ct-channel__body">
                  <strong className="ct-channel__name">Hours</strong>
                  <span className="ct-channel__time">Mon – Sat · 10 AM – 7 PM IST</span>
                </span>
              </div>
            </div>
          </section>

          {/* ── Two-column body ──────────────────────────────────────── */}
          <div className="ct-body">

            {/* ── LEFT: Form ──────────────────────────────────────────── */}
            <section className="ct-form-wrap" aria-labelledby="ct-form-heading">
              <h2 id="ct-form-heading" className="ct-section-title">Send a message</h2>
              <p className="ct-section-sub">We read every message and reply personally.</p>

              {/* Trust badges — tuned for a new 2026 launch */}
              <div className="ct-trust-row" aria-label="Trust indicators">
                <span className="ct-trust-badge">Launching 2026</span>
                <span className="ct-trust-badge">Made in India</span>
                <span className="ct-trust-badge">Free shipping · 7‑day size &amp; defect returns</span>
              </div>

              {sentTo ? (
                <div className="ct-sent" role="status" aria-live="polite" aria-atomic="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <h3 className="ct-sent__title">Message received!</h3>
                  {/* FIX: sentTo captures email before form reset — no more blank */}
                  <p className="ct-sent__sub">
                    We'll reply to <strong>{sentTo}</strong> within 24 hours.
                  </p>
                  <p className="ct-sent__sub">
                    Need faster help?{' '}
                    <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="ct-sent__wa">
                      Chat on WhatsApp
                    </a>
                  </p>
                  <button
                    type="button"
                    className="ct-sent__reset"
                    onClick={() => setSentTo(null)}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  className="ct-form"
                  onSubmit={handleSubmit}
                  noValidate
                  aria-label="Contact form"
                  ref={formRef}
                >
                  {/* Honeypot — FIX: visibility:hidden so no layout contribution */}
                  <div className="ct-form__hp" aria-hidden="true">
                    <label htmlFor="ct-hp">Leave blank</label>
                    <input
                      type="text"
                      id="ct-hp"
                      name="_hp"
                      value={formData._hp}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Name + Email row */}
                  <div className="ct-form__row">
                    <Field
                      id="ct-name"
                      label="Name"
                      required
                      error={errors.name}
                    >
                      <input
                        type="text"
                        id="ct-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`ct-input${errors.name ? ' ct-input--err' : ''}`}
                        placeholder="Your full name"
                        autoComplete="name"
                        aria-required="true"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'ct-name-err' : undefined}
                      />
                    </Field>
                    <Field
                      id="ct-email"
                      label="Email"
                      required
                      error={errors.email}
                    >
                      <input
                        type="email"
                        id="ct-email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        /* FIX: 16px prevents iOS Safari viewport zoom (CLS) */
                        className={`ct-input${errors.email ? ' ct-input--err' : ''}`}
                        placeholder="your@email.com"
                        autoComplete="email"
                        inputMode="email"
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'ct-email-err' : undefined}
                      />
                    </Field>
                  </div>

                  {/* Order number — optional, reduces back-and-forth */}
                  <Field id="ct-order" label="Order number" hint="Optional — include if your question is about an order">
                    <input
                      type="text"
                      id="ct-order"
                      name="orderNumber"
                      value={formData.orderNumber}
                      onChange={handleChange}
                      className="ct-input"
                      placeholder="e.g. EVR-2024-00123"
                      autoComplete="off"
                      inputMode="text"
                    />
                  </Field>

                  {/* Topic select */}
                  <Field
                    id="ct-subject"
                    label="Topic"
                    required
                    error={errors.subject}
                  >
                    <select
                      id="ct-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`ct-input ct-input--select${errors.subject ? ' ct-input--err' : ''}`}
                      aria-required="true"
                      aria-invalid={!!errors.subject}
                      aria-describedby={errors.subject ? 'ct-subject-err' : undefined}
                    >
                      {SUBJECTS.map(s => (
                        <option key={s.value} value={s.value} disabled={!s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  {/* Message with char counter */}
                  <Field
                    id="ct-message"
                    label="Message"
                    required
                    error={errors.message}
                    hint={`${msgLen}/${MSG_MAX}`}
                    hintWarning={msgLen > 0 && msgLen < MSG_MIN}
                  >
                    <textarea
                      id="ct-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={`ct-input ct-input--area${errors.message ? ' ct-input--err' : ''}`}
                      placeholder="How can we help? Include any relevant details."
                      rows={5}
                      maxLength={MSG_MAX}
                      aria-required="true"
                      aria-invalid={!!errors.message}
                      aria-describedby={
                        [errors.message ? 'ct-message-err' : null, 'ct-message-hint']
                          .filter(Boolean).join(' ') || undefined
                      }
                    />
                  </Field>

                  <button
                    type="submit"
                    className="ct-submit"
                    disabled={loading}
                    aria-label={loading ? 'Sending…' : 'Send message'}
                  >
                    {loading
                      ? <><span className="ct-submit__spinner" aria-hidden="true" /> Sending…</>
                      : <>Send Message <IconArrowRight size={15} aria-hidden="true" /></>
                    }
                  </button>
                </form>
              )}
            </section>

            {/* ── RIGHT: Sidebar ──────────────────────────────────────── */}
            <aside className="ct-sidebar" aria-label="Quick help">

              <div className="ct-info-card">
                {/* FIX: was <h2> — sidebar h2s conflicted with main column h2s.
                    Changed to a styled <p> acting as label. */}
                <p className="ct-info-card__label">Quick answers</p>
                <ul className="ct-info-list">
                  {QUICK_LINKS.map(({ label, to, cta }) => (
                    <li key={to} className="ct-info-list__item">
                      <span className="ct-info-list__label">{label}</span>
                      <Link to={to} className="ct-info-list__link">
                        {cta} <IconArrowRight size={11} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ct-info-card">
                <p className="ct-info-card__label">Follow EVORA</p>
                {BRAND.instagram ? (
                  <a
                    href={BRAND.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ct-social-link"
                    aria-label={`Follow ${BRAND_NAME} on Instagram`}
                  >
                    <IconInstagram size={18} aria-hidden="true" />
                    <span>Instagram</span>
                    <IconArrowRight size={13} aria-hidden="true" />
                  </a>
                ) : (
                  <p className="ct-info-card__placeholder">Coming soon</p>
                )}
              </div>

              <div className="ct-trust-note">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <p>Your information is never shared with third parties.</p>
              </div>
            </aside>
          </div>

          {/* ── Related (internal linking) ────────────────────────────────── */}
          <nav className="ct-related" aria-label="Related information">
            <h2 className="ct-related__title">Related information</h2>
            <ul className="ct-related__list" role="list">
              <li><Link to="/faq" className="ct-related__link">FAQ</Link></li>
              <li><Link to="/returns" className="ct-related__link">Returns &amp; Exchanges</Link></li>
              <li><Link to="/shipping" className="ct-related__link">Shipping &amp; Delivery</Link></li>
              <li><Link to="/shop" className="ct-related__link">Shop All</Link></li>
            </ul>
          </nav>
        </main>

        {/* ── WhatsApp sticky CTA (mobile only, appears after scroll delay) ── */}
        {showWaSticky && (
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="ct-wa-sticky"
            aria-label="Chat with us on WhatsApp"
          >
            <IconWhatsApp size={22} aria-hidden="true" />
            <span>WhatsApp us</span>
          </a>
        )}
      </Layout>
    </>
  );
}

// ── Field component ────────────────────────────────────────────
function Field({ id, label, required, error, hint, hintWarning, children }) {
  const errId  = `${id}-err`;
  const hintId = `${id}-hint`;
  return (
    <div className="ct-field">
      <div className="ct-field__top">
        <label htmlFor={id} className="ct-label">
          {label}
          {required && <span className="ct-label__req" aria-label="required"> *</span>}
        </label>
        {hint && (
          <span
            id={hintId}
            className={`ct-field__hint${hintWarning ? ' ct-field__hint--warn' : ''}`}
            aria-live="polite"
          >
            {hint}
          </span>
        )}
      </div>
      {children}
      {error && (
        <span id={errId} className="ct-field-err" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}